import { useRef, useEffect, useState, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { caseStudies as fallbackStudies } from "@/data/caseStudies";
import { trackClick } from "@/lib/analytics";
import CaseStudyCard, { logoSizeOverrides } from "@/components/CaseStudyCard";
import type { CaseStudyCardData } from "@/components/CaseStudyCard";
import excapLogo from "@/assets/case-studies/excap-logo.png";

/* Build a map from fallback data so we can look up local logos by slug/id */
const fallbackLogoMap: Record<string, string> = {};
fallbackStudies.forEach((s) => {
  fallbackLogoMap[s.id] = s.logo;
});

/* Also map slugs that differ between DB and local (DB uses full slugs) */
const slugToFallbackId: Record<string, string> = {
  "whop-highered": "whop",
  "c23-labs": "c23",
  "empire": "empire",
  "humming-ai": "humming",
  "m-r": "mer",
  "chill-bites-drinks": "chill",
  "exceptional-100-framer-site-development": "exceptional"
};

/* Extra logo overrides for DB-only entries that have no fallback data */
const extraLogos: Record<string, string> = {
  "exceptional-100-framer-site-development": excapLogo
};

const PortfolioSection = () => {
  const ref = useRef(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const isInView = useInView(ref, {
    once: true,
    margin: "-50px"
  });
  const [studies, setStudies] = useState<CaseStudyCardData[]>([]);
  const [launchingSlug, setLaunchingSlug] = useState<string | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = window.innerWidth < 768 ? 300 : 340;
    el.scrollBy({
      left: dir === "left" ? -cardWidth : cardWidth,
      behavior: "smooth"
    });
  };

  useEffect(() => {
    const fetchStudies = async () => {
      const { data, error } = await supabase
        .from("case_studies")
        .select("id, slug, title, subtitle, category, cover_image_url, logo_url")
        .order("display_order", { ascending: true });

      if (!error && data && data.length > 0) {
        setStudies(data.map((d) => {
          const fallbackId = slugToFallbackId[d.slug] || d.slug;
          const logo = d.logo_url || extraLogos[d.slug] || fallbackLogoMap[fallbackId] || "";
          return {
            id: d.id,
            slug: d.slug,
            title: d.title,
            subtitle: d.subtitle || "",
            category: d.category || "",
            image: d.cover_image_url || "",
            logo
          };
        }));
      } else {
        setStudies(fallbackStudies.map((s) => ({
          id: s.id,
          slug: s.id,
          title: s.title,
          subtitle: s.subtitle,
          category: s.category,
          image: s.image,
          logo: s.logo
        })));
      }
    };
    fetchStudies();
  }, []);

  return (
    <section id="portfolio" ref={ref} className="section-dark py-16 md:py-24 overflow-visible">
      <div className="px-5 md:px-10 lg:px-14 mb-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
          className="flex items-baseline justify-between"
        >
           <div>
              <p className="font-body text-[9px] font-medium tracking-[0.25em] uppercase text-muted-foreground mb-2">
                02 — Selected Work
              </p>
             <h2 className="font-display text-4xl md:text-5xl italic text-foreground/90 pt-1">
               Portfolio
             </h2>
           </div>
           <div className="flex items-center gap-3">
             <span className="font-body text-ts-2xs text-muted-foreground/60 tracking-wider">
               ({studies.length})
             </span>
            <div className="hidden md:flex items-center gap-1">
              <button
                aria-label="Scroll portfolio left"
                onClick={() => scroll("left")}
                disabled={!canScrollLeft}
                className="p-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                aria-label="Scroll portfolio right"
                onClick={() => scroll("right")}
                disabled={!canScrollRight}
                className="p-1.5 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Horizontal scroll – extra top padding gives lift animation headroom */}
      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-4 overflow-x-auto overflow-y-visible px-5 md:px-10 lg:px-14 pb-8 pt-24 -mt-24 snap-x snap-mandatory md:snap-none"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {studies.map((study, i) => {
          const isLaunching = launchingSlug === study.slug;
          return (
            <motion.div
              key={study.id || study.slug}
              initial={{ opacity: 0, y: 30 }}
              animate={
                isLaunching
                  ? {
                      opacity: [1, 1, 1, 0],
                      y: [0, -40, -60, -90],
                      scale: [1, 1.05, 1.03, 0.95],
                      rotateY: [0, 0, 180, 360],
                      rotateZ: [0, -1.5, 0, 2],
                      z: [0, 60, 100, 140],
                    }
                  : isInView
                  ? { opacity: 1, y: 0 }
                  : {}
              }
              transition={
                isLaunching
                  ? {
                      duration: 1.15,
                      ease: [0.25, 0.9, 0.35, 1],
                      times: [0, 0.3, 0.65, 1],
                    }
                  : { delay: i * 0.08, duration: 0.5 }
              }
              onAnimationComplete={() => {
                if (isLaunching) {
                  navigate(`/work/${study.slug}`);
                }
              }}
              className="flex-shrink-0 w-[75vw] max-w-[320px] md:w-[320px] snap-center md:snap-align-none"
              style={{ perspective: 800, transformStyle: "preserve-3d" }}
            >
              <CaseStudyCard
                study={study}
                asLink={false}
                onClick={(e) => {
                  e.preventDefault();
                  if (!launchingSlug) {
                    trackClick("case_study", study.slug);
                    setLaunchingSlug(study.slug);
                  }
                }}
              />
            </motion.div>
          );
        })}
      </div>

       {/* CTA link */}
       <div className="px-5 md:px-10 lg:px-14 pt-2">
         <Link
           to="/clients"
           className="inline-flex items-center gap-1.5 font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground hover:text-foreground transition-colors border-b border-border hover:border-foreground/50 pb-0.5"
         >
          View Full Client Index
          <span className="text-[10px]">→</span>
        </Link>
      </div>
    </section>
  );
};

export default PortfolioSection;
